import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    Typography,
    Snackbar,
    Pagination,
    IconButton,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent, DialogActions
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import { SponsorshipTierListItem } from "../../data/sponsorInfo";
import {  getEventById } from "../../services/EventInformation";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {deleteSponsorTier, getSponsorshipTiers} from "../../services/SponsorService";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteSponsorshipTierModal from "../sponsorshipComponents/DeleteSponsorshipTierModal";
import AddSponsorshipTierDialog from "../sponsorshipComponents/AddSponsorshipTierDialog";
import EditSponsorshipTierDialog from "../sponsorshipComponents/EditSponsorshipTierDialog";
import DeleteIcon from '@mui/icons-material/Delete';

interface SponsorshipTiersProps {
    eventId: string;
    onBackButtonClick: () => void;
}

export default function SponsorshipTiers({ eventId, onBackButtonClick }: SponsorshipTiersProps) {
    const navigate = useNavigate();
    const [sponsorshipTiers, setSponsorshipTiers] = useState<SponsorshipTierListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [eventName, setEventName] = useState<string>("...");

    // for the delete modal ----
    const [deleteModalOpen, setDeleteModalOpen] = useState(false); // modal visibility
    const [tierToDelete, setTierToDelete] = useState<SponsorshipTierListItem | null>(null); //  selected tier
    const [isDeleting, setIsDeleting] = useState(false); //  delete status
    // -----------------
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [tierToEdit, setTierToEdit] = useState<SponsorshipTierListItem | null>(null);


    useEffect(() => {
        fetchSponsorshipTiers();
    }, [eventId]);

    const fetchSponsorshipTiers = async () => {
        if (!eventId) return;
        setLoading(true);
        try {
            const events = await getEventById(eventId);
            setEventName(events.Name);

            const tiersResponse = await getSponsorshipTiers(eventId);
            const sortedTiers = (tiersResponse || []).sort((a, b) => b.MaxDonation - a.MaxDonation);
            setSponsorshipTiers(sortedTiers);
        } catch (error) {
            console.error("Failed to fetch Sponsorship Tiers:", error);
            setSnackbarMessage("Failed to load Sponsorship Tiers. Please try again later.");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const getCurrentItems = useCallback(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sponsorshipTiers.slice(startIndex, endIndex);
    }, [sponsorshipTiers, currentPage]);

    const handleEdit = (tier: SponsorshipTierListItem) => {
        setTierToEdit(tier);
        setIsEditDialogOpen(true);
    };

    const handleSaveEditedTier = async () => {
        setIsEditDialogOpen(false);
        await fetchSponsorshipTiers();
    };

    // for delete modal -------------------
    const handleDelete = (tier: SponsorshipTierListItem) => {
        setTierToDelete(tier);
        setDeleteModalOpen(true);
        console.log("Delete button clicked for tier:", tier);

    };

    const handleConfirmDelete = async () => {
        if (!tierToDelete) return;

        setIsDeleting(true);
        try {
            const success = await deleteSponsorTier(tierToDelete.SponsorshipTierId, eventId);

            if (success) {
                setSponsorshipTiers((prevTiers) => {
                    const updatedTiers = prevTiers.filter((tier) => tier.SponsorshipTierId !== tierToDelete.SponsorshipTierId);
                    return [...updatedTiers]; // new array reference
                });

                setSnackbarMessage(`${tierToDelete.Name} deleted successfully.`);
            } else {
                setSnackbarMessage("Failed to delete sponsorship tier. Please try again.");
            }

            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error deleting sponsorship tier:", error);
            setSnackbarMessage("An error occurred. Please try again.");
            setSnackbarOpen(true);
        } finally {
            setIsDeleting(false);
            setDeleteModalOpen(false);
            setTierToDelete(null);
        }
    };

    // -------------------------------

    const handleAddTier = () => {
        setIsAddDialogOpen(true);
    };

    const handleSaveTier = async () => {
        setIsAddDialogOpen(false);
        await fetchSponsorshipTiers(); // Refresh list after saving
    };

    const columns: GridColDef<SponsorshipTierListItem>[] = [
        { field: 'Name', headerName: 'Tier Name', width: 200, type: "string" },
        {
            field: 'Description',
            headerName: 'Description',
            flex: 2,
            minWidth: 300,
            type: "string",
            renderCell: (params) => (
                <Box
                    sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        lineHeight: "1.2",
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                    }}
                >
                    {params.value}
                </Box>
            ),
        },
        {
            field: 'MinDonation',
            headerName: 'Min Donation',
            flex: 1,
            maxWidth: 150,
            type: "number",
            renderCell: (params) => (
                <Typography>
                    {params.value ? params.value.toLocaleString("en-US", { style: "currency", currency: "USD" }) : "$0.00"}
                </Typography>
            ),
        },
        {
            field: 'MaxDonation',
            headerName: 'Max Donation',
            flex: 1,
            maxWidth: 150,
            type: "number",
            renderCell: (params) => (
                <Typography>
                    {params.value ? params.value.toLocaleString("en-US", { style: "currency", currency: "USD" }) : "$0.00"}
                </Typography>
            ),
        },
        {
            field: 'Actions',
            headerName: 'Actions',
            width: 150,
            type: "actions",
            flex: 1,
            maxWidth: 120,
            sortable: false,
            align: "right",
            headerAlign: "right",
            renderCell: (params) => (
                <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", width: "100%" }}>
                    <IconButton onClick={() => handleEdit(params.row)}>
                        <EditIcon sx={{ color: "green" }} />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(params.row)}>
                        <DeleteIcon sx={{ color: "red" }} />
                    </IconButton>
                </Stack>
            ),
        }
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
            <Typography variant="h5" fontWeight="bold">{eventName} Sponsorship Tiers</Typography>
            <Stack direction="row-reverse" spacing={100} justifyContent="flex-start">
                <Button variant="contained" className="buttonStyled" color="success" onClick={(handleAddTier)} startIcon={<AddIcon />}>Add Tier</Button>
            </Stack>

            {loading ? (
                <Typography>Loading Sponsorship Tiers...</Typography>
            ) : sponsorshipTiers.length > 0 ? (
                <DataGrid
                    rows={getCurrentItems()}
                    columns={columns}
                    getRowId={(row) => row.SponsorshipTierId}
                    className="datagrid"
                    autoHeight
                    disableRowSelectionOnClick
                    hideFooterPagination={true}
                />
            ) : (
                <Typography>No Sponsorship Tiers found for this event.</Typography>
            )}

            {sponsorshipTiers.length > 0 && (
                <Pagination
                    count={Math.ceil(sponsorshipTiers.length / itemsPerPage)}
                    page={currentPage}
                    onChange={(e, page) => handlePageChange(page)}
                    color="primary"
                    className="pagination"
                />
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />

            <DeleteSponsorshipTierModal
                open={deleteModalOpen}
                tier={tierToDelete}
                isDeleting={isDeleting}
                onClose={() => setDeleteModalOpen(false)}
                onConfirmDelete={handleConfirmDelete}
            />

            <AddSponsorshipTierDialog
                isOpen={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                eventId={eventId}
                existingTiers={sponsorshipTiers}
                onSave={handleSaveTier}
            />

            <EditSponsorshipTierDialog
                isOpen={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                tier={tierToEdit}
                existingTiers={sponsorshipTiers}
                onSave={handleSaveEditedTier}
            />

            <Button onClick={onBackButtonClick} variant="contained">Back to Events</Button>
        </Box>

    );
}